import React from "react";

/**
 * Renders a tone selector when the active genre pack defines tone_options
 * in its ai_guidance block. If the genre has no tone_options (e.g. Women's
 * Beauty), this component renders nothing.
 *
 * Props:
 *   genrePack: object — the full genre pack from /api/genres/{key}
 *   value: string — the currently selected tone key (or "custom")
 *   onChange: fn(key) — called when user picks a preset or "custom"
 *   customValue: string — the free-text custom tone description
 *   onCustomChange: fn(text) — called when the custom textarea changes
 */
const SessionToneSelector = ({ genrePack, value, onChange, customValue, onCustomChange }) => {
  const ai = genrePack?.ai_guidance || {};
  const tones = ai.tone_options || [];
  const allowCustom = !!ai.allow_custom_tone;

  if (tones.length === 0 && !allowCustom) {
    return null;
  }

  return (
    <div data-testid="session-tone-selector">
      <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-3">
        Session Tone
      </label>
      <p className="text-xs text-muted-foreground mb-4">
        Pick the visual direction for this session. The AI will style every recommendation around it.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {tones.map((opt) => {
          const selected = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`text-left p-4 border rounded-sm transition ${
                selected
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:border-primary/30"
              }`}
              data-testid={`session-tone-${opt.key}`}
            >
              <div className="text-sm uppercase tracking-widest mb-1">{opt.label}</div>
              <div className="text-xs text-muted-foreground leading-snug">{opt.ethos}</div>
            </button>
          );
        })}
        {allowCustom && (
          <button
            type="button"
            onClick={() => onChange("custom")}
            className={`text-left p-4 border rounded-sm transition ${
              value === "custom"
                ? "border-primary bg-primary/10 text-primary"
                : "border-border hover:border-primary/30"
            }`}
            data-testid="session-tone-custom"
          >
            <div className="text-sm uppercase tracking-widest mb-1">Custom</div>
            <div className="text-xs text-muted-foreground leading-snug">
              Describe the look in your own words.
            </div>
          </button>
        )}
      </div>

      {value === "custom" && (
        <textarea
          value={customValue || ""}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder="e.g. Black-and-white documentary, lots of grain, no eye contact with camera…"
          className="w-full bg-transparent border border-border focus:border-primary outline-none p-4 text-sm rounded-sm"
          rows="3"
          data-testid="session-tone-custom-input"
        />
      )}
    </div>
  );
};

export default SessionToneSelector;
